import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Copy, RefreshCw, Mic, MoonStar, Mail, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

const base = process.env.REACT_APP_BACKEND_URL;
const copyText = async (t, msg) => { await navigator.clipboard?.writeText(t).catch(() => {}); toast.success(msg); };

const Head = ({ icon: I, title, text }) => (
  <div className="flex items-center gap-3">
    <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400/15 text-gold-300"><I className="w-5 h-5" /></span>
    <div><h3 className="font-semibold text-white">{title}</h3><p className="text-sm text-slate-500">{text}</p></div>
  </div>
);

const UrlRow = ({ url, id, label }) => (
  <div className="mt-3 flex items-center gap-2">
    <code data-testid={`${id}-url`} className="flex-1 min-w-0 truncate rounded-xl border border-slate-200 bg-surface-deep/60 px-3 py-2.5 font-mono text-xs text-slate-700">{url}</code>
    <button data-testid={`${id}-copy`} onClick={() => copyText(url, `${label} copied`)} aria-label={`Copy ${label}`} className="grid place-items-center w-10 h-10 shrink-0 rounded-xl border border-slate-200 text-slate-700 hover:text-white"><Copy className="w-4 h-4" /></button>
  </div>
);

export const CalendarSyncCard = () => {
  const [tok, setTok] = useState(null);
  useEffect(() => { api.get("/settings/feed").then((r) => setTok(r.data?.token ?? null)).catch(() => {}); }, []);
  const reset = async () => { if (!window.confirm("Make a new link? The old calendar and Siri links will stop working.")) return; const { data } = await api.post("/settings/feed/reset"); setTok(data?.token ?? null); toast.success("New private links made"); };
  if (!tok) return null;
  const ics = `${base}/api/feed/${tok}.ics`;
  const webcal = ics.replace(/^https?:\/\//, "webcal://");
  const siri = `${base}/api/feed/${tok}/next`;
  return (
    <>
      <article data-testid="calendar-sync-card" className="card min-w-0 p-5 sm:p-6">
        <Head icon={CalendarPlus} title="iPhone Calendar sync" text="Lessons, homework, tests and exams in your Calendar app. Updates every few hours." />
        <a data-testid="calendar-subscribe" href={webcal} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-2.5 text-sm font-bold text-black hover:bg-gold-300"><CalendarPlus className="w-4 h-4" />Add to iPhone Calendar</a>
        <UrlRow url={ics} id="calendar-feed" label="Calendar link" />
        <p className="mt-2 text-xs text-slate-500">On a computer: Google Calendar → Other calendars → From URL → paste the link. Keep this link private.</p>
        <button data-testid="calendar-reset" onClick={reset} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-white"><RefreshCw className="w-3.5 h-3.5" />Make new private links</button>
      </article>
      <article data-testid="siri-card" className="card min-w-0 p-5 sm:p-6">
        <Head icon={Mic} title={'Siri: "What\'s my next lesson?"'} text="Set it up once with the Shortcuts app." />
        <ol className="mt-4 space-y-1.5 text-sm text-slate-700 list-decimal pl-5">
          <li>Copy the link below.</li>
          <li>Open <b className="text-white">Shortcuts</b> → tap <b className="text-white">+</b> → add <b className="text-white">Get Contents of URL</b> and paste the link.</li>
          <li>Add <b className="text-white">Speak Text</b> after it.</li>
          <li>Name the shortcut <b className="text-white">What's my next lesson</b>. Now say "Hey Siri, what's my next lesson".</li>
        </ol>
        <UrlRow url={siri} id="siri" label="Siri link" />
      </article>
    </>
  );
};

export const BedtimeCard = () => {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/settings/bedtime").then((r) => setB(r.data ?? null)).catch(() => {}); }, []);
  const save = async (patch) => { const next = { ...b, ...patch }; setB(next); try { await api.put("/settings/bedtime", next); toast.success(next.enabled ? `Bedtime nudge at ${next.time} on school nights` : "Bedtime nudge off"); } catch (e) { toast.error(formatApiError(e)); } };
  if (!b) return null;
  return (
    <article data-testid="bedtime-card" className="card min-w-0 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <Head icon={MoonStar} title="Bedtime nudge" text="A gentle notification on school nights to wind down." />
        <Switch data-testid="bedtime-switch" checked={b.enabled} onCheckedChange={(v) => save({ enabled: v })} />
      </div>
      <label className="mt-4 flex items-center gap-3 text-sm text-slate-700">Remind me at
        <select data-testid="bedtime-time" value={b.time} onChange={(e) => save({ time: e.target.value })} className="h-10 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-white">
          {["20:30", "20:45", "21:00", "21:15", "21:30", "21:45", "22:00", "22:15", "22:30", "22:45", "23:00"].map((t) => <option key={t}>{t}</option>)}
        </select>
      </label>
      <p className="mt-2 text-xs text-slate-500">Needs notifications turned on (see Notifications above).</p>
    </article>
  );
};

export const WeeklyReportCard = () => {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/report/weekly").then((r) => setW(r.data ?? null)).catch(() => {}); }, []);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/report/weekly/send"); toast.success(data?.to ? `Report sent to ${data.to}` : "Report sent as a notification"); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (!w) return null;
  const Stat = ({ n, l, id }) => <div data-testid={id} className="rounded-2xl border border-slate-200 bg-surface-2 p-3 text-center"><p className="font-display text-3xl text-gold-300">{n}</p><p className="text-xs text-slate-500">{l}</p></div>;
  return (
    <article data-testid="weekly-report-card" className="card min-w-0 p-5 sm:p-6">
      <Head icon={Mail} title="Sunday weekly report" text="Every Sunday at 18:00: what you finished, your streak and the week ahead." />
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat id="weekly-done" n={`${w.done}/${w.set}`} l="homework done" />
        <Stat id="weekly-streak" n={w.streak} l="day streak" />
        <Stat id="weekly-focus" n={w.focus_minutes} l="focus mins" />
        <Stat id="weekly-cards" n={w.cards_reviewed} l="cards" />
      </div>
      {Array.isArray(w.recap) && w.recap.length > 0 && (
        <div data-testid="weekly-recap" className="mt-4">
          <p className="eyebrow">What you covered this week</p>
          <ul className="mt-2 space-y-2 max-h-72 overflow-y-auto pr-1">
            {w.recap.map((r) => (
              <li key={r.subject} data-testid={`weekly-recap-${r.subject.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-slate-200 bg-surface-2 p-3">
                <p className="text-sm font-bold text-gold-300">{r.subject}</p>
                {(r.items ?? []).slice(0, 6).map((i, k) => <p key={k} className="mt-1 text-sm text-slate-700"><span className="font-mono text-xs text-slate-500">{new Date(i.date).toLocaleDateString("en-GB", { weekday: "short" })}</span> {i.text}</p>)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <button data-testid="weekly-send" onClick={send} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:text-white disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Send me this week's report now</button>
    </article>
  );
};
